import React, { useEffect, useState } from "react";
import { useQuery } from "../../../../utils/useQuery";
import Request from "../../../../utils/fetchUtils";
import { getSessionObject } from "../../../../utils/sessionUtils";
import DgButton from "../../../../components/DgButton";
import Swal from "sweetalert2";
import { Grid } from "@mui/material";
import "../../../../styles/broker-accounts.css";

const TradierImport = ({ showBrokerAccounts }) => {
  const query = useQuery();
  const [loading, setLoading] = useState(false);
  const [importingPositions, setImportingPositions] = useState(false);
  const [brokerAccounts, setBrokerAccounts] = useState(
    JSON.parse(localStorage.session).brokerAccounts || [],
  );
  const [authState, setAuthState] = useState("");

  const processAuthCode = async (code) => {
    try {
      setLoading(true);
      const { email, sub, role } = getSessionObject();

      const redirectUri = window.location.href.split("?")[0];

      const res = await Request(
        "/trading/access",
        {
          code,
          email,
          userId: sub,
          role,
          redirectUri,
        },
        "POST",
      );

      if (res.ok) {
        const session = { ...getSessionObject() };
        session.brokerAccounts = res.data.brokerAccounts;
        localStorage.session = JSON.stringify(session);

        setBrokerAccounts(res.data.brokerAccounts);

        Swal.fire(
          "Account imported successfully",
          "Your Tradier account has been successfully imported. Importing your existing positions...",
          "success",
        );

        await importPositionsToPreexistingFund(res.data.brokerAccounts);
      } else {
        const errorMessage =
          res.message || "An error occurred while importing the account.";
        Swal.fire("Error", errorMessage, "error");
      }
    } catch (error) {
      console.error("Error processing the authorization code:", error);
      Swal.fire(
        "Error",
        "An error occurred while processing the authorization.",
        "error",
      );
    } finally {
      setLoading(false);
    }
  };

  const importPositionsToPreexistingFund = async (accounts) => {
    try {
      setImportingPositions(true);

      const tradierAccount = accounts.find(
        (account) => account.provider.toLowerCase() === "tradier",
      );

      if (!tradierAccount) {
        throw new Error("No Tradier account found");
      }

      const res = await Request(
        "/trading/strategy/preexisting-fund/import-position",
        {
          tradierAccountId: tradierAccount.id,
          tradierAccessToken: tradierAccount.accessToken,
        },
        "POST",
      );

      if (res.ok) {
        Swal.fire(
          "Positions imported successfully",
          "Your existing positions have been imported to your preexisting fund.",
          "success",
        );
      } else {
        const errorMessage =
          res.message || "An error occurred while importing positions.";
        Swal.fire("Error importing positions", errorMessage, "error");
      }
    } catch (error) {
      console.error("Error importing positions:", error);
      Swal.fire(
        "Error",
        "An error occurred while importing your positions.",
        "error",
      );
    } finally {
      setImportingPositions(false);
    }
  };

  const handleImportPositions = async (account) => {
    try {
      const result = await Swal.fire({
        title: "Import Positions",
        text: `Do you want to import existing positions from account ${account.id} to your preexisting fund?`,
        icon: "question",
        showCancelButton: true,
        confirmButtonText: "Yes, import",
        cancelButtonText: "Cancel",
        confirmButtonColor: "#3085d6",
        cancelButtonColor: "#6c757d",
      });

      if (result.isConfirmed) {
        await importPositionsToPreexistingFund([account]);
      }
    } catch (error) {
      console.error("Error in handleImportPositions:", error);
    }
  };

  const generateRandomState = () => {
    return (
      Math.random().toString(36).substring(2, 15) +
      Math.random().toString(36).substring(2, 15)
    );
  };

  const generateAuthorizeUrl = () => {
    const scope = "read,trade,market";
    const { REACT_APP_TRADIER_CLIENT_ID } = process.env;

    const newState = generateRandomState();
    localStorage.setItem("tradierAuthState", newState);

    const redirectUri = window.location.href.split("?")[0];

    return `https://api.tradier.com/v1/oauth/authorize?client_id=${REACT_APP_TRADIER_CLIENT_ID}&scope=${scope}&state=${newState}&redirect_uri=${encodeURIComponent(redirectUri)}`;
  };

  const navigateTradier = () => {
    const url = generateAuthorizeUrl();
    const newWindow = window.open(url, "_blank");
    newWindow.focus();
  };

  useEffect(() => {
    const code = query.get("code");
    const receivedState = query.get("state");

    if (code && receivedState) {
      const savedState = localStorage.getItem("tradierAuthState");
      if (receivedState === savedState) {
        processAuthCode(code);
        localStorage.removeItem("tradierAuthState");
      } else {
        Swal.fire(
          "Security Error",
          "Security verification failed. Please try again.",
          "error",
        );
      }

      if (window.history && window.history.replaceState) {
        const cleanUrl = window.location.href.split("?")[0];
        window.history.replaceState({}, document.title, cleanUrl);
      }
    }
  }, []);

  return (
    <div>
      {showBrokerAccounts && (
        <div className="section-separator" style={{ margin: "30px 0" }}>
          <h2 style={{ color: "#95b2c5", fontWeight: "600" }}>
            Broker Accounts
          </h2>
        </div>
      )}

      {showBrokerAccounts && (
        <div
          className="broker-accounts-section"
          style={{ marginBottom: "20px" }}
        >
          {brokerAccounts && brokerAccounts.length > 0 ? (
            <>
              <h3>Connected Accounts</h3>
              <Grid container spacing={2}>
                {brokerAccounts.map((account, index) => (
                  <Grid item xs={12} md={6} lg={4} key={index}>
                    <div
                      className="account-card"
                      style={{
                        border: "1px solid #e5e5ea",
                        padding: "15px",
                        borderRadius: "8px",
                        marginBottom: "10px",
                      }}
                    >
                      <div className="account-info">
                        <div
                          className="account-provider"
                          style={{
                            fontWeight: "bold",
                            fontSize: "16px",
                            color: "#012B45",
                          }}
                        >
                          {account.provider}
                        </div>
                        <div
                          className="account-id"
                          style={{ fontSize: "14px", color: "#8e8e93" }}
                        >
                          ID: {account.id}
                        </div>
                        <div
                          className="account-environment"
                          style={{
                            fontSize: "12px",
                            color: "#8e8e93",
                            marginTop: "5px",
                          }}
                        >
                          Environment: {account.environment}
                        </div>
                        {account.provider.toLowerCase() === "tradier" && (
                          <div style={{ marginTop: "10px" }}>
                            <DgButton
                              category={"secondary"}
                              size={"small"}
                              text={"Import Positions"}
                              action={() => handleImportPositions(account)}
                              loading={importingPositions}
                            />
                          </div>
                        )}
                      </div>
                    </div>
                  </Grid>
                ))}
              </Grid>
            </>
          ) : (
            <p>No broker accounts connected.</p>
          )}
        </div>
      )}

      {!showBrokerAccounts && (
        <div className="import-broker-button" style={{ marginBottom: "30px" }}>
          <DgButton
            category={"primary"}
            size={"medium"}
            text={"Import Tradier Account"}
            action={navigateTradier}
            loading={loading}
          />
        </div>
      )}
    </div>
  );
};

export default TradierImport;
